import app from './app'
import LZString from 'lz-string'

export interface Logger {
  log: (...data: unknown[]) => void
  debug: (...data: unknown[]) => void
}

type StorageType = 'session' | 'local'

class Cache {
  public showLog = true
  public compress = true
  private logger: Logger = console
  private storage: StorageType = 'session'

  setLogger (logger: Logger): void {
    this.logger = logger
  }

  public local () {
    return this.setStorage('local')
  }

  public session () {
    return this.setStorage('session')
  }

  public getStorageType (): string {
    return this.storage
  }

  private setStorage (storage: StorageType) {
    this.storage = storage

    return this
  }

  private getStorage (): Storage {
    return window[`${this.storage}Storage`]
  }

  async remember<T> (url: string): Promise<T | undefined> {
    if (this.getStorage().getItem(url)) {
      this.log(`get(${url})`)

      return this.get(url)
    }

    this.log(`Getting data from server: ${url} ...`)

    const response = await app.get<T>(url)
    const responseData = response.data

    try {
      const beforeSize = this.size()
      this.set(url, responseData)
      const afterSize = this.size()
      const diffSize = ((afterSize - beforeSize) / 1024).toFixed(1)
      this.log(`set(${url}): ${diffSize} kb`)
    } catch (error) {
      this.log(`Could not store data from ${url} into "` + this.getStorageType() + 'Storage". ' + this.size() + ' bytes')
    }

    return responseData
  }

  forget (url: string): void {
    try {
      this.getStorage().removeItem(url)
    } catch (error) {
      this.log(`No se pudo eliminar el item ${url}`)
    }
  }

  set (key: string, value: unknown): void {
    try {
      let stringValue = JSON.stringify(value)

      if (this.compress) {
        stringValue = LZString.compressToUTF16(stringValue)
      }

      this.getStorage().setItem(key, stringValue)
    } catch (error) {
      this.log(`Could not store data into ${this.getStorageType()}Storage with the key ${key}`)
    }
  }

  get<T> (key: string, fallback: T|null = null) {
    let stringValue = this.getStorage().getItem(key)

    if (stringValue) {
      try {
        if (this.compress) {
          stringValue = LZString.decompressFromUTF16(stringValue)
        }
        if (stringValue) {
          return JSON.parse(stringValue)
        }
        return fallback
      } catch (error) {
        return fallback
      }
    }

    return fallback
  }

  log (message: string): string | void {
    message = `cache.ts (${this.getStorageType()}Storage): ${message}`
    if (this.showLog) {
      this.logger.debug(message)
      return message
    }
  }

  size (): number {
    return unescape(encodeURIComponent(JSON.stringify(this.getStorage()))).length
  }

  clear (): this {
    this.getStorage().clear()

    return this
  }

  keys (): Array<string> {
    return Object.entries(this.getStorage()).map(entry => entry[0])
  }

  all (): Record<string, unknown> {
    const data = {}
    this.keys().forEach(key => {
      data[key] = this.get(key)
    })
    return data
  }
}

export default new Cache()
